/**
 * Verification Constants
 *
 * Contains constants related to verification flows, states, and error codes.
 */

/**
 * Face Verification states
 */
export const FACE_LIVENESS_STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  CAPTURING: 'capturing',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const;

export type FaceVerificationState = typeof FACE_LIVENESS_STATES[keyof typeof FACE_LIVENESS_STATES];

/**
 * Document verification states
 */
export const DOCUMENT_STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  CAPTURING: 'capturing',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const;

export type DocumentState = typeof DOCUMENT_STATES[keyof typeof DOCUMENT_STATES];

/**
 * Verification error codes
 */
export const ERROR_CODES = {
  // Session errors
  SESSION_INVALID: 'session_invalid',
  MISSING_SESSION_TOKEN: 'missing_session_token',
  TOKEN_EXPIRED: 'token_expired',

  // Network errors
  NETWORK_ERROR: 'network_error',
  API_ERROR: 'api_error',

  // Face Verification errors
  FACE_LIVENESS_FAILED: 'face_liveness_failed',
  USER_CANCELED: 'user_canceled',
  CAMERA_PERMISSION_DENIED: 'camera_permission_denied',
  DEVICE_UNSUPPORTED: 'device_unsupported',
  FACE_POST_API_FAILED: 'face_post_api_failed',
  FACE_GET_API_FAILED: 'face_get_api_failed',

  // Legal ID errors
  LEGAL_ID_VERIFICATION_FAILED: 'legal_id_verification_failed',
  LEGAL_ID_INVALID: 'legal_id_invalid',
} as const;

export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES];

/**
 * iProov event names
 */
export const IPROOV_EVENTS = {
  READY: 'ready',
  STARTED: 'started',
  STREAMING: 'streaming',
  PROGRESS: 'progress',
  PASSED: 'passed',
  FAILED: 'failed',
  ERROR: 'error',
  CANCELED: 'canceled',
  UNSUPPORTED: 'unsupported',
  PERMISSION: 'permission',
  PERMISSION_DENIED: 'permission_denied',
} as const;

/**
 * PostMessage types
 */
export const MESSAGE_TYPES = {
  VERIFICATION_COMPLETE: 'VERIFICATION_COMPLETE',
  VERIFICATION_ERROR: 'VERIFICATION_ERROR',
} as const;

export const MESSAGE_SOURCE = 'idme-verification' as const;

/**
 * Legal ID capture-flow providers — single source of truth for provider values.
 */
export const LEGAL_ID_PROVIDERS = {
  veriff: 'veriff',
  regula: 'regula',
} as const;

export type LegalIdProvider = typeof LEGAL_ID_PROVIDERS[keyof typeof LEGAL_ID_PROVIDERS];
